import { useEffect, useState } from "react";
import api from "@/lib/api";
import DOMPurify from "dompurify";
import { diffWords } from "diff";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, RotateCcw, Trash2, GitCompare, ArrowLeftRight } from "lucide-react";

export default function VersionHistory({ chapterId, onRestored }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [compareWith, setCompareWith] = useState(null);
  const [mode, setMode] = useState("preview"); // preview | compare

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chapters/${chapterId}/versions`);
      setVersions(data);
      setSelected(data[0] || null);
    } catch {
      toast.error("Failed to load versions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [open, chapterId]);

  const restore = async (v) => {
    if (!confirm("Restore this version? Current content will be saved as a new auto-snapshot.")) return;
    try {
      const { data } = await api.post(`/chapters/${chapterId}/versions/${v.version_id}/restore`);
      toast.success("Restored");
      setOpen(false);
      onRestored && onRestored(data);
    } catch {
      toast.error("Restore failed");
    }
  };

  const del = async (v) => {
    if (!confirm("Delete this version permanently?")) return;
    try {
      await api.delete(`/chapters/${chapterId}/versions/${v.version_id}`);
      setVersions((vs) => vs.filter((x) => x.version_id !== v.version_id));
      if (selected?.version_id === v.version_id) setSelected(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const stripToText = (html) => {
    if (!html) return "";
    return html
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<\/?(p|div|h[1-6]|li|blockquote|br)[^>]*>/gi, "\n\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/[ \t]+/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="chapter-versions-btn">
          <History className="w-4 h-4 mr-1.5" /> Versions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="chapter-versions-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Version history</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="versions-loading">Loading…</p>
        ) : versions.length === 0 ? (
          <p className="py-8 text-center text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="versions-empty">
            No snapshots yet. Use &quot;Save snapshot&quot; in the editor to start a version history.
          </p>
        ) : (
          <div className="grid sm:grid-cols-[260px_1fr] gap-4 flex-1 overflow-hidden">
            <ul className="space-y-2 overflow-y-auto pr-2" data-testid="versions-list">
              {versions.map((v) => (
                <li key={v.version_id}>
                  <button
                    onClick={() => setSelected(v)}
                    className="w-full text-left p-3 rounded transition-colors"
                    style={{
                      background: selected?.version_id === v.version_id ? "var(--bf-surface)" : "transparent",
                      border: "1px solid var(--bf-border)",
                    }}
                    data-testid={`version-row-${v.version_id}`}
                  >
                    <p className="text-sm font-medium truncate">{v.label || "(unlabelled snapshot)"}</p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--bf-text-muted)" }}>
                      {v.saved_by_name || "—"} · {new Date(v.saved_at).toLocaleString()}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--bf-text-muted)" }}>
                      {v.word_count?.toLocaleString() || 0} words
                    </p>
                  </button>
                </li>
              ))}
            </ul>
            <div className="overflow-y-auto" data-testid="versions-preview">
              {selected ? (
                <>
                  <div className="flex items-center justify-between mb-3 sticky top-0 bg-white pb-2 gap-2 flex-wrap" style={{ borderBottom: "1px solid var(--bf-border)" }}>
                    <div className="min-w-0">
                      <p className="font-serif text-lg leading-tight truncate">{selected.title || "(no title)"}</p>
                      <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                        {new Date(selected.saved_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <div className="flex rounded overflow-hidden" style={{ border: "1px solid var(--bf-border)" }} data-testid="versions-mode-toggle">
                        <button
                          onClick={() => setMode("preview")}
                          className="px-2 py-1 text-xs"
                          style={{ background: mode === "preview" ? "var(--bf-primary)" : "white", color: mode === "preview" ? "white" : "inherit" }}
                          data-testid="versions-mode-preview"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => setMode("compare")}
                          className="px-2 py-1 text-xs flex items-center gap-1"
                          style={{ background: mode === "compare" ? "var(--bf-primary)" : "white", color: mode === "compare" ? "white" : "inherit" }}
                          data-testid="versions-mode-compare"
                        >
                          <GitCompare className="w-3 h-3" /> Diff
                        </button>
                      </div>
                      <Button size="sm" onClick={() => restore(selected)} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid={`version-restore-btn-${selected.version_id}`}>
                        <RotateCcw className="w-4 h-4 mr-1.5" /> Restore
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => del(selected)} data-testid={`version-delete-btn-${selected.version_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {mode === "compare" ? (
                    <div data-testid="versions-compare-view">
                      <div className="flex items-center gap-2 mb-3 text-xs" style={{ color: "var(--bf-text-muted)" }}>
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                        <span>Compare</span>
                        <strong style={{ color: "inherit" }}>{(selected.label || "snapshot").slice(0, 30)}</strong>
                        <span>with</span>
                        <select
                          value={compareWith?.version_id || ""}
                          onChange={(e) => {
                            const v = versions.find((x) => x.version_id === e.target.value);
                            setCompareWith(v || null);
                          }}
                          className="border rounded px-2 py-1"
                          style={{ borderColor: "var(--bf-border)" }}
                          data-testid="versions-compare-select"
                        >
                          <option value="">— pick a snapshot —</option>
                          {versions.filter((v) => v.version_id !== selected.version_id).map((v) => (
                            <option key={v.version_id} value={v.version_id}>
                              {(v.label || "snapshot").slice(0, 40)} · {new Date(v.saved_at).toLocaleDateString()}
                            </option>
                          ))}
                        </select>
                      </div>
                      {!compareWith ? (
                        <p className="text-sm py-6" style={{ color: "var(--bf-text-muted)" }} data-testid="versions-compare-empty">
                          Pick a snapshot above to see word-level diff.
                        </p>
                      ) : (
                        <div className="grid sm:grid-cols-2 gap-4" data-testid="versions-diff-grid">
                          <div className="text-sm">
                            <p className="label-eyebrow mb-2">{(compareWith.label || "older snapshot").slice(0, 40)}</p>
                            <pre className="whitespace-pre-wrap font-serif p-3 rounded" style={{ background: "#FEF2F2", border: "1px solid #FECACA", lineHeight: 1.6 }} data-testid="versions-diff-left">
                              {diffWords(stripToText(compareWith.content || ""), stripToText(selected.content || "")).map((part, i) => (
                                part.added ? null :
                                  <span
                                    key={`L-${i}-${part.value.slice(0, 12)}`}
                                    style={part.removed ? { background: "#FCA5A5", textDecoration: "line-through" } : {}}
                                  >
                                    {part.value}
                                  </span>
                              ))}
                            </pre>
                          </div>
                          <div className="text-sm">
                            <p className="label-eyebrow mb-2">{(selected.label || "this snapshot").slice(0, 40)}</p>
                            <pre className="whitespace-pre-wrap font-serif p-3 rounded" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", lineHeight: 1.6 }} data-testid="versions-diff-right">
                              {diffWords(stripToText(compareWith.content || ""), stripToText(selected.content || "")).map((part, i) => (
                                part.removed ? null :
                                  <span
                                    key={`R-${i}-${part.value.slice(0, 12)}`}
                                    style={part.added ? { background: "#86EFAC" } : {}}
                                  >
                                    {part.value}
                                  </span>
                              ))}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className="prose-book max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.content || "<p><em>(empty)</em></p>", { USE_PROFILES: { html: true } }) }}
                    />
                  )}
                </>
              ) : (
                <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>Pick a snapshot to preview.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
